import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, GTDStage, Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {UserSettings} from '@client/user-settings'
import {DragData} from '@common/drag-data'
import {Tracking, TrackingEvent} from '@common/tracking'
import {getAllUsersTasks} from '@components/my-tasks/utils'
import {PageComponent} from '@components/page/page-component'
import {PredefinedFilter} from '@components/right-panel/search/search-store'
import {SearchTop} from '@components/right-panel/search/search-top'
import {useForceUpdate, useLocalStorage, useSyncEvent} from '@containers/hooks'
import {withUpdaters} from '@containers/updaters'
import {SimpleUpdater} from '@containers/updaters/simple-updater'
import {SyncUpdater} from '@containers/updaters/sync-updater'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {Task} from '@extensions/task'
import {MyTasksBase} from './my-tasks-base'
import {TaskPage} from './my-tasks-conf'
import {TaskItem} from './task-item'

const state: {
  up?: () => void
  draggedId?: DataObjectId
} = {}

const Tasks = withUpdaters(
  SyncUpdater({companyType: 'current', filter: '*', debounceTime: 50}),
  SyncUpdater({
    companyType: 'profile',
    filter: () => globals.getCompanyId(),
    debounceTime: 50
  }),
  SimpleUpdater(up => (state.up = up))
)(({isOthers = false}: {isOthers?: boolean}) => {
  const companyId = globals.getCompanyId()
  const box = Sync.getCurrentSandbox()
  const [page, setPage] = useLocalStorage<TaskPage>(
    `my-tasks-page-others`,
    'by-people'
  )
  const tasks = getAllUsersTasks(page)

  const forceUpdate = useForceUpdate()

  useSyncEvent(
    'profile',
    {filter: globals.getCompanyId(), debounceTime: 100},
    forceUpdate
  )
  useSyncEvent('current', {filter: '*', debounceTime: 200}, forceUpdate)

  const toggleCompleted = (task: TaskItem) =>
    Task.changeCompleted({...task, companyId}, !task.completedAt, 'my-tasks')

  const setStage = (task: TaskItem, stage: GTDStage, reason: string) => {
    const t = {...task, companyId}
    Task.insertInGTD(t, stage, reason)
  }

  const setOrder = (key: string, order: DataObjectId[]) => {
    UserSettings.setOrder(key, order)
  }

  const itemClick = (task: TaskItem) => () => {
    if (box) {
      const hub = box.get(task.id) as Hub
      if (hub) {
        ChatUtils.openChatById(task.id)
        void Tracking.send(TrackingEvent.myTasksOpenTaskViaMyTaskSection)
      }
    }
  }

  const setDragged = (e: React.DragEvent, id: DataObjectId) => {
    if (id) {
      DragData.set(e, {type: 'my-task', id})
    }
    state.draggedId = id
    state.up?.()
  }

  return (
    <MyTasksBase
      key={page}
      isOthers={isOthers}
      tasks={tasks}
      toggleCompleted={toggleCompleted}
      setStage={setStage}
      setOrder={setOrder}
      itemClick={itemClick}
      draggedId={state.draggedId}
      setDrag={setDragged}
      page={page}
      setPage={setPage}
    />
  )
})

const TwTitleWrapper = tw.div`
  h-56
  py-12
  flex
  items-center
  gap-16
`

const TwTitle = tw.div`
  text-sm-semibold
  text-panel-text-primary
  flex-none
  flex
  items-center
`

const availablePredefinedFilters: PredefinedFilter[] = [
  'everywhere',
  'task-titles',
  'custom'
]

const Title = ({title}: {title: string}) => (
  <TwTitleWrapper>
    <TwTitle>{title}</TwTitle>
    <SearchTop
      defaultPredefinedFilter="everywhere"
      availablePredefinedFilters={availablePredefinedFilters}
    />
  </TwTitleWrapper>
)

export const OthersTasks = () => {
  const title = <Title title={Lang.msg.taskPage.othersTasks} />
  return <PageComponent title={title}>{<Tasks isOthers />}</PageComponent>
}
